/**
 * Domain Layer - Query Service
 * React Query configuration and utilities
 */

import { QueryClient } from "@tanstack/react-query";
import { siteConfig } from "@/config/site";
import { LIMITS } from "@/constants";
import type { ErrorWithStatus } from "@/shared/data/api";

/**
 * Creates a configured QueryClient with optimal defaults
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: siteConfig.queryStaleTime,
        gcTime: siteConfig.queryGcTime,

        retry: (failureCount, error) => {
          // Don't retry on client errors (4xx)
          const err = error as ErrorWithStatus;
          if (
            err instanceof Error &&
            typeof err.statusCode === "number" &&
            err.statusCode >= 400 &&
            err.statusCode < 500
          ) {
            return false;
          }
          // Retry up to configured limit
          return failureCount < LIMITS.MAX_QUERY_RETRIES;
        },

        refetchOnReconnect: true,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

/**
 * Helper to generate query keys for consistent caching
 * Uses feature-based architecture to organize cache keys
 */
export function createQueryKeys(feature: string) {
  return {
    all: [feature] as const,
    list: () => [...createQueryKeys(feature).all, "list"] as const,
    detail: (id: string | number) =>
      [...createQueryKeys(feature).all, "detail", id] as const,
    search: (params: Record<string, unknown>) =>
      [...createQueryKeys(feature).all, "search", params] as const,
  };
}

/**
 * Common query keys used across the application
 */
export const queryKeys = {
  auth: createQueryKeys("auth"),
  dashboard: createQueryKeys("dashboard"),
  user: createQueryKeys("user"),
  // Add more features here as needed
};

/**
 * Singleton QueryClient instance
 * For use in provider and server-side operations
 */
export const queryClient = createQueryClient();
